import React, { useEffect, useState } from 'react';
import { Label } from 'reactstrap';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import styled from 'styled-components';
import BackButton from '../../../components/custom/BackButton';
import ActionIcon from '../../../components/custom/ActionIcon';
import { IUser, UnitIp } from '../../../globals/interfaces';
import { createdUpdatedInfo, getTsDate } from '../../../utils';
import Cleave from 'cleave.js/react';
import 'cleave.js/dist/addons/cleave-phone.i18n';
import IpAddressInput from './IpAddressInput';
import { HandleCreateUpdateIp } from '.';
import useTypedForm from '../../../components/custom/hookForm/useTypedForm';
import _ from 'lodash';
import { yupResolver } from '@hookform/resolvers/yup';
import { createSchema, updateSchema } from './yupSchema';

interface Props {
    modal: boolean;
    setModal: (state: boolean) => void;
    selectedIp: UnitIp | undefined;
    handleCreateUpdateIp: HandleCreateUpdateIp;
    deleteIpAddress: () => Promise<void>;
    unit: string;
    user: IUser;
}

export default function ModalManageIp({
    modal,
    setModal,
    selectedIp,
    handleCreateUpdateIp,
    deleteIpAddress,
    unit,
    user
}: Props) {

    const update = Boolean(selectedIp);
    const [validIp, setValidIp] = useState(false);

    const {
        formState: {
            isValid,
            isDirty
        },
        handleSubmit,
        reset,
        trigger,
        setValue,
        watch,
        FormInput
    } = useTypedForm<UnitIp>({
        resolver: update ? yupResolver(updateSchema) : yupResolver(createSchema)
    });

    useEffect(() => {
        const date = getTsDate();
        if (update) {
            reset({
                ...selectedIp,
                modifiedBy: user.s_email,
                modifiedAt: date
            });
        } else {
            reset({
                unit,
                createdBy: user.s_email,
                createdAt: date,
                modifiedBy: user.s_email,
                modifiedAt: date
            });
        }
        setValidIp(false);
        trigger();
    }, [selectedIp, update, modal, user.s_email, unit]);

    const setIpAddress = (value: string) => setValue('ipAddress', value, {
        shouldDirty: true,
        shouldValidate: true
    });

    const toggle = () => setModal(!modal);

    return (
        <Modal isOpen={modal} toggle={toggle}>
            <ModalHeader className={'d-flex'}>
                <HeaderContainer>
                    <BackButton onClick={toggle} />
                    <h4 className={'pl-2'}>{update ? 'Update' : 'Create'} IP Address</h4>
                </HeaderContainer>
            </ModalHeader>
            <ModalBody className={'mx-auto'}>
                <IpAddressInput 
                    initialValue={_.get(selectedIp, 'ipAddress', '')}
                    setValue={setIpAddress}
                    setValid={setValidIp}
                />
                <div className={'mt-3'}>
                    <FormInput 
                        label={'Notes'}
                        name={'notes'}
                        type={'textarea'}
                    />
                </div>
            </ModalBody>
            <ExpandedFooter>
                <FooterContentContainer>
                    <Label>
                        {createdUpdatedInfo(selectedIp)}
                    </Label>
                    <FooterButtonsContainer update={update}>
                        {
                            update && (
                                <ActionIcon 
                                    type={'delete'} 
                                    onClick={() => deleteIpAddress()} 
                                />
                            )
                        }
                        <ActionIcon 
                            disabled={!isValid || !validIp || !isDirty}
                            type={'save'} 
                            onClick={handleSubmit(handleCreateUpdateIp)} 
                        />
                    </FooterButtonsContainer>
                </FooterContentContainer>
            </ExpandedFooter>
        </Modal>
    );
}

const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
`;

const ExpandedFooter = styled(ModalFooter)`
    width: 100%;
`;

const FooterContentContainer = styled.div`
    width: 100%;
`;

const FooterButtonsContainer = styled.div<{ update?: boolean }>`
    width: 100%;
    display: flex;
    justify-content: ${p => p.update ? 'space-between' : 'flex-end'};
`;
